import { ReplicantError } from '@play-co/replicant';
import StateObserver from 'src/StateObserver';
import PopupCheats, { Item } from 'src/game/cheats/PopupCheats';
import { CheatData } from 'src/game/cheats/cheats.sequences';
import { onError } from 'src/lib/errors';
import { closeAllPopups, closePopup } from 'src/lib/popups/popupOpenClose';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  animDuration,
  getApplication,
  getFooter,
  waitForItPromise,
} from 'src/lib/utils';
import {
  PuzzleResults,
  match2MaxLevel,
} from 'src/replicant/match2/match2.ruleset';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import {
  getMatch2TimedRewardRemainingTime,
  isMatch2TimedRewardActive,
} from 'src/replicant/match2/match2.getters';
import { duration, getPartOfTime } from 'src/replicant/utils/duration';
import { match2TournamentsCheats } from './tournament/match2-tournament.cheats';
import { match2DailyChallengeCheats } from './dailychallenge/match2-dailychallenge.cheats';
import { match2RacesCheats } from './races/races.cheats';
import { getGameScene } from './game/getGameScene';
import { match2SaveStateCheats } from './savestate/match2-savestate.cheats';
import { match2ShopBundlesCheats } from './shopbundles/match2-shopbundles.cheats';
import { BlockGenAlgorithm, match2BlockGenAlgorithms } from './game/defs/map';
import { getCheatForceBlockGen, setCheatForceBlockGen } from './match2.util';
import { match2BundleSaleCheats } from './bundlesale/match2-bundlesale.cheats';
import { Match2TimedRewardType } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { getMatch2TimedRewardTimestampKey } from 'src/replicant/match2/rewards/match2-rewards.getters';
import { match2MagicLampsCheats } from './magiclamps/magiclamps.cheats';
import { editorSetStartingPowerups } from './editor/editor.utils';
import { match2StreakBattleRoyaleCheats } from 'src/game/streakbattleroyale/streakbattleroyale.cheats';
import { PuzzleMapImporter } from './game/util/PuzzleMapImporter';
import {
  SaveStateData,
  encodeSaveStateData,
} from './savestate/match2-savestate.data';
import { match2CardsCheats } from 'src/game/cards/cards.cheats';
import {
  animateFrenzyObjective,
  animatePuzzleRewards,
} from 'src/game/animation/rewardAnimation';
import { superLollipopCheats } from 'src/game/superLollipop/superLollipop.cheats';
import { weeklyLeaderboardCheats } from 'src/game/weeklyLeaderboard/weeklyLeaderboard.cheats';
import { treasureExpressCheats } from 'src/game/treasureExpress/treasureExpress.cheats';
import { championsLeagueCheats } from 'src/game/championsLeague/championsLeague.cheats';
import { isMatch2FrenzyUnlocked } from 'src/replicant/match2/frenzy/match2-frenzy.getters';
import { ToggleSwitcher } from 'src/game/components/shared/ToggleSwitcher';
import { rescueLevelCheats } from 'src/game/rescueLevel/rescueLevel.cheats';
import { match2BuddiesCheats } from 'src/game/buddies/buddies.cheats';
import { match2DreamDriftCheats } from 'src/game/dreamDrift/dreamDrift.cheats';
import { match2FrenzyPostGame } from './frenzy/match2-frenzy.sequence';

const MATCH2_LEVEL = 'match2 Level';
const MATCH2_RESET_TIPS = 'match2 Reset Tips';
const MATCH2_TIMED_REWARDS = 'match2 Timed Rewards';
const MATCH2_ADD_FRENZY = 'match2 Add Frenzy';
const MATCH2_EXTRA_MOVES = 'Match2 Extra Moves';
const MATCH2_BLOCK_GEN = 'match2 BlockGen';
const MATCH2_RESET_FRENZY = 'match2 Reset Frenzy';
const MATCH2_EDITOR = 'match2 Editor';
const MATCH2_GAMEPLAY_CHEATS = 'match2 Gameplay Cheats';
const MATCH2_BURU_STREAK = 'match2 Buru Streak';
const MATCH2_STARTING_POWERUPS = 'match2 Starting Powerups';
const MATCH2_CHANGE_BLOCKGEN = 'match2 Change BlockGen Algorithm';
const MATCH2_CUSTOM_LEVELS = 'match2 Custom Levels';
const MATCH2_ANIMATION = 'match2 Animation';

const match2TimedRewardCheatDuration = duration({ minutes: 5 });

async function addFrenzyProgress(data: CheatData) {
  const amount = data.amount!;
  console.log('Adding', amount, 'Match2 Frenzy Progress');

  closeAllPopups();

  await StateObserver.invoke.cheat_setMatch2AddFrenzyProgress(amount);

  const frenzyRewards = await match2FrenzyPostGame({
    result: 'win',
  });

  await animateFrenzyObjective({ count: amount, rewards: frenzyRewards });
}

async function addExtraMovesToInventory(data: CheatData) {
  const amount = data.amount!;
  await StateObserver.invoke.cheat_addMatch2ExtraMovesInInventotry(amount);
  console.log(
    amount ? 'Adding' : 'Removing',
    amount,
    `extra moves ${amount ? 'to' : 'from'} the inventory - remaining:`,
    StateObserver.getState().user.match2.inventory.extraMoves,
  );
}

export type BlockGenAlgorithmCheats = BlockGenAlgorithm | 'default';
function setBlockgen(data: CheatData) {
  const blockgen = data.blockgen === 'default' ? undefined : data.blockgen;
  setCheatForceBlockGen(blockgen);
}

async function playCustomLevel(data: string) {
  const mapDef = new PuzzleMapImporter(data).import();
  if (!mapDef) return;

  const state: SaveStateData = {
    level: StateObserver.getState().user.match2.level,
    visualMoves: 0,
    moves: mapDef.moves,
    goalCounts: mapDef.goals,
    mapDef: data,
    score: 0,
    continues: 0,
    extraMoves: 0,
    socialContinueMoves: 0,
    adContinueMoves: 0,
    usedBoosters: {},
    blocksBroken: {},
    powerBlocksCreated: {},
    blocksBrokenAnalytics: {},
  };

  // Store custom level as a save state, that will be set up after reload.
  await StateObserver.invoke.saveMatch2GameplayState({
    level: StateObserver.getState().user.match2.level,
    data: encodeSaveStateData(state),
  });

  window.location.reload();
}

// All keys here should start with the `match2` prefix - goes into cheats.ruleset.ts
export const match2CheatsList = (filter?: Match2CheatFlags) => {
  const user = StateObserver.getState().user;
  const cheats: Record<string, unknown> = {};

  Object.assign(cheats, rescueLevelCheats.list());

  cheats[MATCH2_EDITOR] = [
    {
      text: MATCH2_EDITOR,
      color: 'secondary',
      cheat: async () => {
        getApplication().screenManager.showScreen('minigame', {
          minigame: 'match2-editor',
        });
        closePopup('popupMatch2Cheats');
      },
    },
  ];

  cheats[MATCH2_CUSTOM_LEVELS] = [
    {
      text: 'Play Broken Level',
      color: 'secondary',
      cheat: async () => {
        await playCustomLevel(
          'm40o[da25]r[]s[bb,bg,bp,br,by]g[o9r9c[,,,,,,,,,,,,,,,,,,,,da,da,da,da,da,,,,,da,da,da,da,da,,,,,da,da,da,da,da,,,,,da,da,da,da,da,,,,,da,da,da,da,da,,,,,,,,,,,,,,,,,,,,]]',
        );
        closePopup('popupMatch2Cheats');
      },
      level: true,
    },
    {
      text: 'Play Objects Level',
      color: 'secondary',
      cheat: async () => {
        await playCustomLevel(
          'm40o[kt4da4fb4l4al4tva4zo2ha6]r[]s[bb,br,by]g[o9r9c[rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,al,al,al,al,da2,da2,da2,da2,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,tvb,tvb,tvb,tvb,fb,fb,fb,fb,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,ha,ha,ha,cf,kt,kt,kt,kt,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,pcs,pcs,pcs,cf,l,l,l,l,rs,rs,rs,rs,rs,rs,rs,rs,rs]]',
        );
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
    {
      text: 'Play Animals Level',
      color: 'secondary',
      cheat: async () => {
        await playCustomLevel(
          'm40o[ca4d2sp2S4bf4n4mfu4sh3]r[]s[bb,br,by]g[o9r9c[rs,rs,rs,rs,sp,rs,ca,ca,rs,rs,rs,rs,rs,sp,rs,ca,ca,rs,d,d,rs,rs,rs,rs,rs,rs,sh,rs,rs,rs,n,n,rs,S,S,rs,rs,rs,rs,n,n,rs,S,S,rs,rs,rs,rs,rs,rs,rs,rs,rs,sh,rs,rs,rs,fu,fu,rs,w,w,rs,rs,rs,rs,fu,fu,rs,w,w,rs,rs,rs,rs,rs,rs,rs,rs,rs,sh]]',
        );
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
    {
      text: 'Play Nature Level',
      color: 'secondary',
      cheat: async () => {
        await playCustomLevel(
          'm40o[P9fo5ch5dv5m5sl4hi4a5]r[]s[bb,br,by]g[o9r9c[rs|sl,rs|sl,rs,fo,fo,fo,fo,fo,P,rs|sl,rs|sl,rs,rs,rs,rs,rs,rs,P,rs,rs,rs,ch,ch,ch,ch,ch,P,rs,rs,rs,rs,rs,rs,rs,rs,P,rs,rs,rs,dv,dv,dv,dv,dv,P,rs,rs,rs,rs,rs,rs,rs,rs,P,rs,rs,rs,m,m,m,m,m,P,rs|ti,rs|ti,rs,rs,rs,rs,rs,rs,P,rs|ti,rs|ti,rs,a,a,a,a,a,P]]',
        );
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
    {
      text: 'Play Food Level',
      color: 'secondary',
      cheat: async () => {
        await playCustomLevel(
          'm40o[ka4st4tf4po4vi4ce4s1cd4C1pl1ps4]r[]s[bb,br,by]g[o9r9c[ps,rs,rs,rs,rs,rs,rs,rs,rs,rs,hz,hz,hz,hz,ka,ka,ka,ka,ps,rs,rs,rs,rs,rs,rs,rs,rs,rs,ce,ce,ce,ce,st,st,st,st,ps,rs,rs,rs,rs,rs,rs,rs,rs,rs,po,po,po,po,tc,tc,tc,tc,ps,rs,rs,rs,rs,rs,rs,rs,rs,rs,pl,$,C,$,s,$,cb,$,rs,$,$,$,$,$,$,$,$]]',
        );
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
    {
      text: 'Play Soda & Fireworks Level',
      color: 'secondary',
      cheat: async () => {
        await playCustomLevel(
          'm40o[fc1cl6sr2fb6]r[]s[bb,br,by]g[o9r9c[rs,rs,rs,rs,rs,rs,fc,$,$,rs,rs,rs,rs,rs,rs,$,$,$,rs,rs,rs,rs,rs,rs,$,$,$,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,cl,cl,cl,rs,rs,rs,rs,rs,rs,cl,cl,cl,rs,rs,rs,rs,rs,rs,rs,rs,rs,rs,fb,fb,fb,rs,rs,rs,sb,$,rs,fb,fb,fb,rs,rs,rs,sr,$]]',
        );
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
    {
      text: 'Play Bubbles Level',
      color: 'secondary',
      cheat: async () => {
        await playCustomLevel(
          'm40o[c13bu5rb9shb9bt1]r[]s[bb,br,by]g[o9r9c[shb,shb,shb,rb,rb,rb,c,c,c,shb,shb,shb,rb,rb,rb,c,c,c,shb,shb,shb,rb,rb,rb,c,c,c,$,$,$,$,$,$,$,$,t,$,$,$,$,$,$,bt,$,t,$,$,$,$,$,$,$,$,t,$,$,$,$,$,$,$,$,t,$,$,$,$,$,$,$,$,$,$,$,$,$,bu,bu,bu,bu,bu]]',
        );
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
    {
      text: 'Play Big Blocks Level',
      color: 'secondary',
      cheat: async () => {
        await playCustomLevel(
          'm40o[A1C1s1fc1wa1cta1cd4]r[]s[bb,br,by]g[o9r9c[wa,$,$,$,$,$,$,A,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,cb,$,s,$,C,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,cta,$,$,$,$,$,fc,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$,$]]',
        );
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
  ];

  cheats[MATCH2_GAMEPLAY_CHEATS] = [
    {
      text: 'Toggle Debug Panel',
      color: 'secondary',
      cheat: async () => {
        getGameScene()?.cheatSystem?.toggleLog();
        closePopup('popupMatch2Cheats');
      },
      gameplay: true,
    },
    {
      text: 'Big Lollipop Explosion',
      color: 'secondary',
      cheat: async () => {
        getGameScene()?.cheatSystem?.bigLolipopExplosion();
        closePopup('popupMatch2Cheats');
      },
      gameplay: true,
    },
    {
      text: 'Big Lollipop Explosion x3',
      color: 'secondary',
      cheat: async () => {
        getGameScene()?.cheatSystem?.bigLolipopExplosion(3);
        closePopup('popupMatch2Cheats');
      },
      gameplay: true,
    },
    {
      text: 'Place Bomb',
      color: 'secondary',
      cheat: async () => {
        getGameScene()?.cheatSystem?.placeBomb();
        closePopup('popupMatch2Cheats');
      },
      gameplay: true,
    },
    {
      text: 'Place Rocket Horizontal',
      color: 'secondary',
      cheat: async () => {
        getGameScene()?.cheatSystem?.placeRocketHorizontal();
        closePopup('popupMatch2Cheats');
      },
      gameplay: true,
    },
    {
      text: 'Place Rocket Vertical',
      color: 'secondary',
      cheat: async () => {
        getGameScene()?.cheatSystem?.placeRocketVertical();
        closePopup('popupMatch2Cheats');
      },
      gameplay: true,
    },
    {
      text: 'Place Lollipop',
      color: 'secondary',
      cheat: async () => {
        getGameScene()?.cheatSystem?.placeCube();
        closePopup('popupMatch2Cheats');
      },
      gameplay: true,
    },
    {
      text: 'Lose game',
      color: 'secondary',
      cheat: async () => {
        closePopup('popupMatch2Cheats');
        await waitForItPromise(animDuration * 2);
        void getGameScene()?.cheatSystem?.loseGame();
      },
      gameplay: true,
    },
    {
      text: 'Win game',
      color: 'secondary',
      cheat: async () => {
        closePopup('popupMatch2Cheats');
        await waitForItPromise(animDuration * 2);
        void getGameScene()?.cheatSystem?.winGame();
      },
      gameplay: true,
    },
    {
      text: 'Trigger Replication Error',
      color: 'secondary',
      cheat: async () => {
        closePopup('popupMatch2Cheats');
        await waitForItPromise(animDuration * 2);

        // Exercise the same recovery path used for live replication errors.
        await onError(
          new ReplicantError(
            'Cheat-triggered replication error',
            'replication_error',
            undefined,
          ),
        );
      },
      gameplay: true,
    },
    {
      text: 'Last 6 Moves',
      color: 'secondary',
      cheat: async () => {
        closePopup('popupMatch2Cheats');
        void getGameScene()?.cheatSystem?.setMoves(6);
      },
      gameplay: true,
    },
  ];

  cheats[MATCH2_LEVEL] = [
    {
      cheat: async (button: { amount?: number }) => {
        const level = (button?.amount || 0) + 1;
        await StateObserver.invoke.cheat_setMatch2Level(level);
        getFooter()?.minigameButton.playLevelUp();
      },
      text: 'Match 2 Level',
      color: 'cancel',
      level: true,
    },
  ];

  cheats[MATCH2_RESET_TIPS] = [
    {
      text: MATCH2_RESET_TIPS,
      color: 'secondary',
      cheat: async () => {
        await StateObserver.invoke.cheat_resetMatch2Tips();
      },
    },
  ];

  cheats[MATCH2_BURU_STREAK] = [
    {
      text: 'Max Buru Streak',
      color: 'secondary',
      cheat: async () => {
        await StateObserver.invoke.cheat_buruStreakMax();
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
    {
      text: 'Reset Buru Streak',
      color: 'secondary',
      cheat: async () => {
        await StateObserver.invoke.cheat_buruStreakReset();
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
  ];

  cheats[MATCH2_STARTING_POWERUPS] = [
    {
      text: 'All Powerups',
      color: 'secondary',
      cheat: async () => {
        editorSetStartingPowerups(['bomb', 'rocket', 'cube']);
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
    {
      text: 'No Powerups',
      color: 'secondary',
      cheat: async () => {
        editorSetStartingPowerups([]);
        closePopup('popupMatch2Cheats');
      },
      editor: true,
    },
  ];

  const algorithms = match2BlockGenAlgorithms.filter(
    (a) => a !== 'rubberbanding',
  );
  cheats[MATCH2_CHANGE_BLOCKGEN] = [
    ...algorithms.map((blockgen) => ({
      text: blockgen,
      color: 'secondary',
      cheat: async () => {
        getGameScene()?.cheatSystem?.setBlockGenAlgorithm(
          blockgen as BlockGenAlgorithm,
        );
        getGameScene()?.cheatSystem?.toggleLog(true);
        closePopup('popupMatch2Cheats');
      },
      gameplay: true,
    })),
  ];

  if (isMatch2FrenzyUnlocked(user)) {
    const blockGenAlgoOptions = algorithms.slice() as BlockGenAlgorithmCheats[];
    blockGenAlgoOptions.unshift('default');

    cheats[MATCH2_BLOCK_GEN] = blockGenAlgoOptions.map((blockgen) => ({
      cheat: setBlockgen,
      localeText: () => blockgen,
      blockgen,
      on: getCheatForceBlockGen() === blockgen,
    }));

    cheats[MATCH2_ADD_FRENZY] = [
      { cheat: addFrenzyProgress, localeText: () => '5', amount: 5 },
      { cheat: addFrenzyProgress, localeText: () => '10', amount: 10 },
      { cheat: addFrenzyProgress, localeText: () => '15', amount: 15 },
      { cheat: addFrenzyProgress, localeText: () => '20', amount: 20 },
      { cheat: addFrenzyProgress, localeText: () => '25', amount: 25 },
      { cheat: addFrenzyProgress, localeText: () => '30', amount: 30 },
      { cheat: addFrenzyProgress, localeText: () => '50', amount: 50 },
      { cheat: addFrenzyProgress, localeText: () => '100', amount: 100 },
      { cheat: addFrenzyProgress, localeText: () => '225', amount: 225 },
      { cheat: addFrenzyProgress, localeText: () => '250', amount: 250 },
      { cheat: addFrenzyProgress, localeText: () => '500', amount: 500 },
      { cheat: addFrenzyProgress, localeText: () => '1000', amount: 1000 },
      { cheat: addFrenzyProgress, localeText: () => '2500', amount: 2500 },
      { cheat: addFrenzyProgress, localeText: () => '6000', amount: 6000 },
      { cheat: addFrenzyProgress, localeText: () => '15000', amount: 15000 },
      { cheat: addFrenzyProgress, localeText: () => '20000', amount: 20000 },
    ];

    cheats[MATCH2_RESET_FRENZY] = [
      {
        text: 'Reset Match2 Frenzy',
        color: 'secondary',
        cheat: async () => {
          await StateObserver.invoke.resetMatch2Frenzy();
          const frenzyBar =
            getApplication().screenManager.header.match2FrenzyBar;
          frenzyBar.syncState();
        },
      },
    ];

    cheats[MATCH2_EXTRA_MOVES] = [
      { cheat: addExtraMovesToInventory, localeText: () => '5', amount: 5 },
      { cheat: addExtraMovesToInventory, localeText: () => '10', amount: 10 },
      { cheat: addExtraMovesToInventory, localeText: () => '15', amount: 15 },
      { cheat: addExtraMovesToInventory, localeText: () => '20', amount: 20 },
      { cheat: addExtraMovesToInventory, localeText: () => '25', amount: 25 },
      { cheat: addExtraMovesToInventory, localeText: () => '30', amount: 30 },
      { cheat: addExtraMovesToInventory, localeText: () => '50', amount: 50 },
      { cheat: addExtraMovesToInventory, localeText: () => '100', amount: 100 },
      { cheat: addExtraMovesToInventory, localeText: () => '200', amount: 200 },

      { cheat: addExtraMovesToInventory, localeText: () => '-5', amount: -5 },
      { cheat: addExtraMovesToInventory, localeText: () => '-10', amount: -10 },
      { cheat: addExtraMovesToInventory, localeText: () => '-15', amount: -15 },
      { cheat: addExtraMovesToInventory, localeText: () => '-20', amount: -20 },
      { cheat: addExtraMovesToInventory, localeText: () => '-25', amount: -25 },
      { cheat: addExtraMovesToInventory, localeText: () => '-30', amount: -30 },
      { cheat: addExtraMovesToInventory, localeText: () => '-50', amount: -50 },
      {
        cheat: addExtraMovesToInventory,
        localeText: () => '-100',
        amount: -100,
      },
      {
        cheat: addExtraMovesToInventory,
        localeText: () => '-200',
        amount: -200,
      },
    ];

    cheats[MATCH2_TIMED_REWARDS] = [
      {
        cheat: toggleTimedReward,
        type: 'energy',
        text: getTimedRewardText('energy'),
        on: isTimedRewardActive('energy'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
      {
        cheat: toggleTimedReward,
        type: 'bomb',
        text: getTimedRewardText('powerups-bomb'),
        on: isTimedRewardActive('powerups-bomb'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
      {
        cheat: toggleTimedReward,
        type: 'rocket',
        text: getTimedRewardText('powerups-rocket'),
        on: isTimedRewardActive('powerups-rocket'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
      {
        cheat: toggleTimedReward,
        type: 'cube',
        text: getTimedRewardText('powerups-cube'),
        on: isTimedRewardActive('powerups-cube'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
      {
        cheat: toggleTimedReward,
        type: 'dart',
        text: getTimedRewardText('boosters-dart'),
        on: isTimedRewardActive('boosters-dart'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
      {
        cheat: toggleTimedReward,
        type: 'bullet',
        text: getTimedRewardText('boosters-bullet'),
        on: isTimedRewardActive('boosters-bullet'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
      {
        cheat: toggleTimedReward,
        type: 'drill',
        text: getTimedRewardText('boosters-drill'),
        on: isTimedRewardActive('boosters-drill'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
      {
        cheat: toggleTimedReward,
        type: 'roulette',
        text: getTimedRewardText('boosters-roulette'),
        on: isTimedRewardActive('boosters-roulette'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
      {
        cheat: toggleTimedReward,
        type: 'frenzyMultiplierX2',
        text: getTimedRewardText('multiplier-frenzy'),
        on: isTimedRewardActive('multiplier-frenzy'),
        duration: match2TimedRewardCheatDuration,
        color: 'cancel',
      },
    ];
  }

  cheats[MATCH2_ANIMATION] = [
    {
      text: 'Sample Reward Animations',
      color: 'secondary',
      cheat: async () => {
        closeAllPopups();
        await animatePuzzleRewards({
          stars: 3,
          rewards: [
            { type: 'bPoints', amount: 100 },
            { type: 'gems', amount: 80 },
            { type: 'card', cardId: 'burumoments_beach_01' },
            { type: 'feature', subType: 'frenzy_objective', amount: 50 },
            { type: 'feature', subType: 'race_points', amount: 30, raceId: '' },
            { type: 'feature', subType: 'team_chest_event', amount: 5 },
          ],
          dailyChallengeProgress: [
            {
              goal: {
                type: 'basicBlocks',
                subType: 'basicYellow',
              },
              amount: 10,
            },
            {
              goal: {
                type: 'basicBlocks',
                subType: 'basicGreen',
              },
              amount: 10,
            },
            {
              goal: {
                type: 'powerBlocks',
                subType: 'rocket',
              },
              amount: 10,
            },
            {
              goal: {
                type: 'powerBlocks',
                subType: 'bomb',
              },
              amount: 10,
            },
            {
              goal: {
                type: 'powerBlocks',
                subType: 'cube',
              },
              amount: 10,
            },
            {
              goal: { type: 'movesRemaining' },
              amount: 10,
            },
            {
              goal: { type: 'levelCompletes', subType: 'default' },
              amount: 1,
            },
            {
              goal: { type: 'levelCompletes', subType: 'maxBuruStreak' },
              amount: 1,
            },
          ],
          weeklyLeaderboardProgress: true,
          puzzleAttempts: 0,
        });
      },
    },
  ];

  Object.assign(cheats, match2TournamentsCheats.list());
  Object.assign(cheats, match2DailyChallengeCheats.list());
  Object.assign(cheats, match2RacesCheats.list());
  Object.assign(cheats, match2SaveStateCheats.list());
  Object.assign(cheats, match2ShopBundlesCheats.list());
  Object.assign(cheats, match2BundleSaleCheats.list());
  Object.assign(cheats, match2MagicLampsCheats.list());
  Object.assign(cheats, match2StreakBattleRoyaleCheats.list());
  Object.assign(cheats, match2CardsCheats.list());
  Object.assign(cheats, superLollipopCheats.list());
  Object.assign(cheats, weeklyLeaderboardCheats.list());
  Object.assign(cheats, treasureExpressCheats.list());
  Object.assign(cheats, championsLeagueCheats.list());
  Object.assign(cheats, match2BuddiesCheats.list());
  Object.assign(cheats, match2DreamDriftCheats.list());

  return filter ? filterCheats(cheats, filter) : cheats;
};

function isTimedRewardActive(type: Match2TimedRewardType) {
  const user = StateObserver.getState().user;
  const now = StateObserver.now();
  return isMatch2TimedRewardActive(user, type, now);
}

function getTimeText(time: number) {
  const h = getPartOfTime(time, 'hours', true).toString().padStart(2, '0');
  const m = getPartOfTime(time, 'minutes', true).toString().padStart(2, '0');
  const s = getPartOfTime(time, 'seconds', true).toString().padStart(2, '0');
  return `${h}:${m}:${s}`;
}

function getTimedRewardText(type: Match2TimedRewardType) {
  const user = StateObserver.getState().user;
  const now = StateObserver.now();
  const on = isMatch2TimedRewardActive(user, type, now);
  if (on) {
    const remaining = getMatch2TimedRewardRemainingTime(user, type, now);
    const remainingStr = getTimeText(remaining);
    return `${type} ${remainingStr}`;
  } else {
    return `${type} OFF`;
  }
}

async function toggleTimedReward(
  data: { type: string },
  switcher: ToggleSwitcher,
  api: PopupCheats,
  extras?: { title?: ButtonScaleViewWithText },
) {
  const now = StateObserver.now();
  const type = data.type as Match2TimedRewardType;
  const key = getMatch2TimedRewardTimestampKey(type);

  await StateObserver.invoke.cheat_setMatch2TimedReward({
    key,
    timestamp: isTimedRewardActive(type) ? 0 : now + duration({ minutes: 5 }),
  });

  api.updateSwitcher(switcher, isTimedRewardActive(type));

  if (extras?.title) {
    extras.title.text = getTimedRewardText(type);
  }
}

// Mount UI for match2 specific cheats - goes into PopupCheats.ts
export const match2CheatsUI = (args: {
  key: string;
  popup: PopupCheats;
  item: Item;
  title: LangBitmapFontTextView;
  setting: CheatData[];
  parent: Item | null;
  uiType?: string;
}) => {
  // Ignore items that are not starting with `match2` prefix
  if (!args.key.startsWith('match2')) return;

  if (args.key === MATCH2_LEVEL) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup.createMultipleButtons(
      args.item,
      match2MaxLevel + 1,
      args.setting,
    );
  }

  if (args.key === MATCH2_RESET_FRENZY) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup['createHorizontalButtons'](args.item, args.setting);
  }

  if (args.key === MATCH2_RESET_TIPS) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup['createHorizontalButtons'](args.item, args.setting);
  }

  if (args.key === MATCH2_EDITOR) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup['createHorizontalButtons'](args.item, args.setting);
  }

  if (args.key === MATCH2_CUSTOM_LEVELS) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup.createVerticalButtons(args.item, args.setting);
  }

  if (args.key === MATCH2_GAMEPLAY_CHEATS) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup.createVerticalButtons(args.item, args.setting);
  }

  if (args.key === MATCH2_ADD_FRENZY || args.key === MATCH2_BLOCK_GEN) {
    args.item.dropdown = args.popup['createDropdown'](args.item, args.setting);

    const viewToExtend = args.parent ?? args.item;

    let originalHeight: number = viewToExtend.style.height;
    args.item.dropdown.onOpen = () => {
      viewToExtend.updateOpts({
        zIndex: 1,
        height:
          originalHeight +
          args.item.dropdown.items.length * args.item.dropdown.itemHeight,
      });
    };
    args.item.dropdown.onClose = () => {
      viewToExtend.updateOpts({ zIndex: 0, height: originalHeight });
    };

    args.popup['createApplyButton'](args.item);
  }

  if (args.key === MATCH2_TIMED_REWARDS) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      // height: 20,
    });
    args.popup['createSwitchers'](args.item, args.setting);
  }

  if (args.key === MATCH2_BURU_STREAK) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup.createVerticalButtons(args.item, args.setting);
  }

  if (args.key === MATCH2_STARTING_POWERUPS) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup.createVerticalButtons(args.item, args.setting);
  }

  if (args.key === MATCH2_CHANGE_BLOCKGEN) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup.createVerticalButtons(args.item, args.setting);
  }

  if (args.key === MATCH2_ANIMATION) {
    args.title.updateOpts({
      width: args.item.style.width - 40,
      height: 20,
      y: 8,
      align: 'center',
    });
    args.popup.createVerticalButtons(args.item, args.setting);
  }

  match2TournamentsCheats.ui(args);
  match2DailyChallengeCheats.ui(args);
  match2RacesCheats.ui(args);
  match2SaveStateCheats.ui(args);
  match2ShopBundlesCheats.ui(args);
  match2BundleSaleCheats.ui(args);
  match2MagicLampsCheats.ui(args);
  match2StreakBattleRoyaleCheats.ui(args);
  match2CardsCheats.ui(args);
  superLollipopCheats.ui(args);
  weeklyLeaderboardCheats.ui(args);
  treasureExpressCheats.ui(args);
  championsLeagueCheats.ui(args);
  rescueLevelCheats.ui(args);
  match2BuddiesCheats.ui(args);
  match2DreamDriftCheats.ui(args);
};

export type Match2CheatFlags = {
  /** Cheats that can be used during gameplay */
  gameplay?: boolean;
  /** Cheats that should be defined prior to gameplay, like starting powerups */
  editor?: boolean;
  /** Cheats that relates to match2 levels */
  level?: boolean;
};

function filterCheats(
  cheats: Record<string, unknown>,
  filter: Match2CheatFlags,
) {
  // Filter out non-gameplay cheats.
  for (const key in cheats) {
    const sublist = cheats[key] as Match2CheatFlags[];
    let i = sublist.length;
    while (i--) {
      const subitem = sublist[i];
      let remove = false;

      if (!remove && filter.gameplay !== undefined) {
        remove = !subitem.gameplay === filter.gameplay;
      }

      if (!remove && filter.editor !== undefined) {
        remove = !subitem.editor === filter.editor;
      }

      if (!remove && filter.level !== undefined) {
        remove = !subitem.level === filter.level;
      }
      // Remove the cheat if it's not matching the filter.
      if (remove) {
        sublist.splice(i, 1);
      }
    }
    // Remove the category if becomes empty.
    if (!sublist.length) {
      delete cheats[key];
    }
  }

  return cheats;
}
